import {
    DECORATION_BY_TERM_TYPE,
    TERM_TYPES,
} from 'components/TopKeywordsSection/termTypes';
import { GOOGLE_METADATA_BY_KEY } from 'utils/metrics';
import InlineTable from '../primitives/InlineTable';
import { buildBreadcrumb } from '../lib/breadcrumbFormat';
import { columnLabel } from '../lib/formatColumnValue';
import { buildRows, excludedTerms, parentName } from '../lib/tableHelpers';
import type { Column, TableModel, VisualProps, VisualWidget } from '../types';

const NAME_KEY = 'term';
const TYPE_LABEL = 'Search Query';

/**
 * The pair's data_store rows carry a single figure the drawer shows (search
 * volume) with one hardcoded volume-descending ordering, so this is a
 * single-column table — built literally rather than through buildColumns,
 * which structurally returns a volume + growth pair.
 */
const COLUMNS: Column[] = [
    {
        key: GOOGLE_METADATA_BY_KEY.volume.key,
        label: columnLabel(
            GOOGLE_METADATA_BY_KEY.volume.key,
            GOOGLE_METADATA_BY_KEY,
        ),
        isSort: true,
        isGrowth: false,
    },
];

/**
 * The dashboard drawer's query treatment: every row is a search query, so all
 * get the keyword curly quotes (Google's list has no per-row term type). A
 * query already carrying the opening quote is treated as decorated.
 */
function decorateQuery(name: string): string {
    const decoration = DECORATION_BY_TERM_TYPE[TERM_TYPES.KEYWORD];
    if (decoration.before && name.startsWith(decoration.before)) return name;
    return `${decoration.before}${name}${decoration.after ?? ''}`;
}

function buildRelatedSearchQueriesModel({
    input,
    output,
}: VisualProps): TableModel {
    // The pair's second identity — the backend stamps it on every related-terms
    // result; its absence means the payload shape drifted, so fail loud.
    const relatedName = output?.result?.related_entity_name;
    if (!relatedName) {
        throw new Error(
            'related search queries visual: output is missing ' +
                'result.related_entity_name',
        );
    }
    const anchorName = parentName(output);
    const rows = buildRows(
        output,
        COLUMNS,
        NAME_KEY,
        GOOGLE_METADATA_BY_KEY,
    ).map((row) => ({
        ...row,
        name: decorateQuery(row.name),
    }));
    return {
        breadcrumb: buildBreadcrumb(output, TYPE_LABEL),
        // Title carries both entities: "Cerave + Eczema Search Queries"
        // (CSS capitalizes) — the drawer section this list mirrors.
        title: {
            parent: [anchorName, relatedName].filter(Boolean).join(' + '),
            noun: 'search queries',
            ...excludedTerms(input, output),
        },
        link: output?.result?.dashboard_url,
        nameColumnHeader: TYPE_LABEL,
        columns: COLUMNS,
        // Rows are search phrases someone typed, not entity names.
        preserveNameCase: true,
        rows,
    };
}

const RelatedSearchQueriesInlineTable = ({
    Frame,
    dense,
    onExcludeRow,
    ...props
}: VisualProps) => (
    <InlineTable
        {...buildRelatedSearchQueriesModel(props)}
        Frame={Frame}
        dense={dense}
        onExcludeRow={onExcludeRow}
    />
);

const widget: VisualWidget = {
    match: (input) =>
        input?.data_source === 'google' &&
        input?.entity_relationship === 'related_terms',
    Component: RelatedSearchQueriesInlineTable,
};

export default widget;
